import TableWrappper from "src/pages/reservaciones/inicio/sections/TableWrapper/TableWrapper"
import FlexibleTable, {
    SortFilterValue,
    TableFilter,
} from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import TablePaginatorWrapper from "src/shared/components/data-display/FlexibleTable/sections/TablePaginatorWrapper/TablePaginatorWrapper"
import FloatButon from "src/shared/components/layout/FloatButon/FloatButon"
import { useFetch } from "src/shared/hooks/useFetch"
import { useProfile } from "src/shared/hooks/useProfile"
import ReportWrapper from "../../components/report-wrapper/ReportWrapper"
import CardReports from "../../components/card-reports/CardReports"
import { CardReportResume } from "../../components/card-report/CardReport"
import { formatCurrency, formatCurrencyToFixed } from "src/shared/hooks/formatCurrency"
import { tableSkeletonRows } from "src/shared/components/layout/skeleton/TableSkeleton/TableSkeleton"
import {
    FlexibleTableHeaderColumn,
    FlexibleTableRow,
} from "src/shared/components/data-display/FlexibleTable/flexible-table-items.interface"
import useFormattedMeta from "../hooks/useFormattedMeta"
import useDedupedRefetch from "../hooks/useDedupedRefetch"
import { useEffect, useMemo, useState } from "react"
import useDownloadPdf from "src/shared/hooks/useDownloadPdf"
import { Stringify } from "src/shared/types/Stringify"
import { ReporteOrdenesType, TablaVentasArticulo } from "./Ordenes.type"
import useOrigenHeader from "./hooks/useOrigenHeader"
import useEstatusOrdenHeader from "./hooks/useEstatusOrdenHeader"
import useEstatusPagoHeader from "./hooks/useEstatusPagoHeader"
import useTipoVentaHeader, { TIPOS_VENTA } from "./hooks/useTipoVentaHeader"
import useTurnoHeader from "../hooks/useTurnoHeader"
import useColaboradoresHeader from "../hooks/useColaboradoresHeader"
import useMetodoPagoHeader from "../hooks/useMetodoPagoHeader"
import { formatText } from "src/shared/hooks/formatTextOpcions"
import { capitalize } from "src/shared/helpers/capitalize"
import reportSelectedHandler from "../../helpers/reportSelectedHandler"
import { ReportsOptions } from "../../Reports.type"
import ReportLoaderModal from "../../components/report-loader/ReportLoaderModal"
import StatusBadge from "./components/StatusBadge"
import OrdenesDetail from "./detail/OrdenesDetail"
import useOrdenesDetail from "./detail/hooks/useOrdenesDetail"

// Renderiza el contenido de una celda separando por "/" y colocando cada
// parte en su propia línea (ej. "Junior Villa / 16" -> dos renglones).
const MultilineCell = ({ text, format }: { text: string; format?: (value: string) => string }) => {
    const parts = text
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean)

    if (!parts.length) {
        return <>-</>
    }

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            {parts.map((part, idx) => (
                <span key={idx}>{format ? format(part) : part}</span>
            ))}
        </div>
    )
}

const ReporteOrdenes = ({ apiDateFilter }: { apiDateFilter: string[] | null }) => {
    const { hotel_id } = useProfile()
    const { download } = useDownloadPdf()

    const { data, refetch, load } = useFetch<ReporteOrdenesType>("/reportes/tabla_ordenes", {
        startFetch: false,
    })

    const [currentPage, setcurrentPage] = useState(1)

    const { header: origenHeader, habitaciones, mesas } = useOrigenHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "origen",
        valueToDisplay: "Origen",
    })

    const estatusOrdenHeader = useEstatusOrdenHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "estatus_orden",
        valueToDisplay: "Estatus de orden",
    })

    const estatusPagoHeader = useEstatusPagoHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "estatus_pago",
        valueToDisplay: "Estatus de pago",
    })

    const turnoHeader = useTurnoHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "turno",
        valueToDisplay: "Turno",
    })

    const tipoVentaHeader = useTipoVentaHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "tipo_venta",
        valueToDisplay: "Tipo de venta",
    })

    const responsableHeader = useColaboradoresHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "atendio",
        valueToDisplay: "Atendió",
    })

    const cobraHeader = useColaboradoresHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "cobro",
        valueToDisplay: "Cobró",
    })

    const comandoHeader = useColaboradoresHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "comanda",
        valueToDisplay: "Comandó",
    })

    const metodosPagosHeader = useMetodoPagoHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "metodos_pago",
        valueToDisplay: "Método de pago",
    })

    const [sortValue, setsortValue] = useState<SortFilterValue | null>(null)
    const [filterValues, setfilterValues] = useState<TableFilter[] | null>(null)

    // A partir de los orígenes seleccionados (habitaciones / mesas) se agrega el
    // filtro adicional tipo_venta: "Room service" para habitaciones y "Restaurante"
    // para mesas. Mostrador no agrega ningún tipo_venta.
    // Si el usuario filtró la columna "Tipo de venta" de forma explícita, esa
    // selección manda y no se deriva nada a partir del origen.
    const augmentedFilterValues = useMemo<TableFilter[] | null>(() => {
        if (!filterValues?.length) {
            return filterValues
        }

        if (filterValues.some((f) => f.fromHeader === "tipo_venta")) {
            return filterValues
        }

        const origenFilters = filterValues.filter((f) => f.fromHeader === "origen")
        if (!origenFilters.length) {
            return filterValues
        }

        const habitacionesSet = new Set(habitaciones)
        const mesasSet = new Set(mesas)

        const tipoVentas: string[] = []
        if (origenFilters.some((f) => habitacionesSet.has(f.filter))) {
            tipoVentas.push(TIPOS_VENTA.roomService)
        }
        if (origenFilters.some((f) => mesasSet.has(f.filter))) {
            tipoVentas.push(TIPOS_VENTA.restaurante)
        }

        if (!tipoVentas.length) {
            return filterValues
        }

        const tipoVentaFilters: TableFilter[] = tipoVentas.map((filter, idx) => ({
            filter,
            fromHeader: "tipo_venta",
            idx,
        }))

        return [...filterValues, ...tipoVentaFilters]
    }, [filterValues, habitaciones, mesas])

    const formattedMetaValues = useMemo<{
        filterValues: TableFilter[] | null
        sortValue: SortFilterValue | null
        headerValues: Stringify<TablaVentasArticulo>[]
    }>(
        () => ({
            filterValues: augmentedFilterValues,
            sortValue,
            headerValues: [
                "orden",
                "estatus_orden",
                "origen",
                "turno",
                "tipo_venta",
                "fecha_venta",
                "atendio",
                "cobro",
                "comanda",
                "productos",
                "total",
                "metodos_pago",
                "estatus_pago",
            ],
        }),
        [augmentedFilterValues, sortValue]
    )

    const { formattedMeta } = useFormattedMeta(formattedMetaValues)

    useDedupedRefetch(
        refetch,
        apiDateFilter && {
            hotel_id,
            fecha_inicio: apiDateFilter[0],
            fecha_fin: apiDateFilter[1],
            take: 15,
            page: currentPage,
            ...(formattedMeta?.filtro ? { filtro: JSON.stringify(formattedMeta.filtro) } : {}),
            ...(formattedMeta?.orden ? { orden: JSON.stringify(formattedMeta.orden) } : {}),
        }
    )

    const headers: FlexibleTableHeaderColumn[] = [
        { value: "orden", valueToDisplay: "Orden" },
        estatusOrdenHeader,
        origenHeader,
        turnoHeader,
        tipoVentaHeader,
        { value: "fecha_venta", valueToDisplay: "Fecha de venta", sort: true },
        responsableHeader,
        cobraHeader,
        comandoHeader,
        { value: "productos", valueToDisplay: "Productos", sort: true },
        { value: "total", valueToDisplay: "Total", sort: true },
        metodosPagosHeader,
        estatusPagoHeader,
    ]

    const { skeletonRows } = tableSkeletonRows({ headers })

    const rows = data?.respuesta.tabla_ventas_articulos

    const { selectedRow, isOpen, openDetail, closeDetail } = useOrdenesDetail(rows)

    const tableItems: FlexibleTableRow[] = rows?.map((orden, index) => ({
        // goTo lo recibe el evento goTo de la tabla al dar click en la fila, se usa para abrir el detalle
        goTo: String(index),
        className: "ordenes__row--clickable",
        value: [
            { value: orden.orden || "-", className: "reports-cell" },
            { value: <StatusBadge estatus={orden.estatus_orden} />, className: "reports-cell" },
            { value: <MultilineCell text={orden.origen || ""} format={capitalize} />, className: "reports-cell" },
            { value: orden.turno || "-", className: "reports-cell" },
            { value: orden.tipo_venta || "-", className: "reports-cell" },
            { value: orden.fecha_venta || "-", className: "reports-cell" },
            { value: orden.atendio || "-", className: "reports-cell" },
            { value: orden.cobro || "-", className: "reports-cell" },
            { value: orden.comanda || "-", className: "reports-cell" },
            { value: orden.productos ?? "-", className: "reports-cell" },
            { value: formatCurrencyToFixed(orden.total), className: "reports-cell" },
            { value: <MultilineCell text={orden.metodos_pago || ""} format={formatText} />, className: "reports-cell" },
            { value: <StatusBadge estatus={orden.estatus_pago} />, className: "reports-cell" },
        ],
    }))

    const { setSelectedSection } = reportSelectedHandler()

    useEffect(() => {
        setSelectedSection(ReportsOptions.ordenes)
    }, [])

    useEffect(() => {
        setcurrentPage(1)
    }, [apiDateFilter])

    return (
        <>
            {load &&
                <ReportLoaderModal isOpen />
            }
            <ReportWrapper height="calc(100dvh - 125px)">
                <CardReports>
                    <CardReportResume
                        title1="Total ventas"
                        value1={formatCurrency(Number(data?.dashboard?.total_ventas || 0))}
                        title2="Total"
                        value2={data?.dashboard?.total_productos || 0}
                    />
                    <CardReportResume
                        title1="Room service"
                        value1={formatCurrency(Number(data?.dashboard?.venta_room_service || 0))}
                        title2="Total"
                        value2={data?.dashboard?.cantidad_room_service || 0}
                    />
                    <CardReportResume
                        title1="Venta mostrador"
                        value1={formatCurrency(Number(data?.dashboard?.venta_mostrador || 0))}
                        title2="Total"
                        value2={data?.dashboard?.cantidad_mostrador || 0}
                    />
                    <CardReportResume
                        title1="Venta restaurante"
                        value1={formatCurrency(Number(data?.dashboard?.venta_restaurante || 0))}
                        title2="Total"
                        value2={data?.dashboard?.cantidad_restaurante || 0}
                    />
                </CardReports>
                <TablePaginatorWrapper
                    className="historial-propinas__table__wrapper"
                    currentPage={currentPage}
                    onChange={(p) => setcurrentPage(p)}
                    pages={data?.respuesta.total_paginas || 0}
                    style={{
                        justifyContent: data?.respuesta?.tabla_ventas_articulos?.length ? "flex-start" : "center",
                        borderRadius: "0 0 20px 0",
                    }}
                >
                    <TableWrappper>
                        <FlexibleTable
                            goTo={(rowKey) => {
                                if (load) {
                                    return
                                }
                                openDetail(rowKey)
                            }}
                            containerStyle={{
                                ...(load ? { overflow: "hidden" } : {})
                            }}
                            emptyState={{
                                headerIcon: "PieChartFilled",
                                titile: "Aún no hay reportes disponibles",
                                subTitle:
                                    "Todavía no hay datos para mostrar en esta sección. Una vez que se genere un reporte, lo verás aquí.",
                            }}
                            onSelectedFilters={(v) => {
                                setcurrentPage(1)
                                setfilterValues(v)
                            }}
                            onSort={(v) => {
                                setcurrentPage(1)
                                setsortValue(v)
                            }}
                            containterClassName="historial-propinas__table"
                            tableItems={{
                                headers,
                                rows: load ? skeletonRows : tableItems,
                            }}
                        />
                    </TableWrappper>
                    <FloatButon
                        icon="Download"
                        onAdd={() => {
                            if (!apiDateFilter?.length) {
                                return
                            }
                            download({
                                endpoint: "/reportes/tabla_ordenes",
                                params: {
                                    hotel_id,
                                    fecha_inicio: apiDateFilter[0],
                                    fecha_fin: apiDateFilter[1],
                                    descargar: true,
                                    ...(formattedMeta?.filtro ? { filtro: JSON.stringify(formattedMeta.filtro) } : {}),
                                    ...(formattedMeta?.orden ? { orden: JSON.stringify(formattedMeta.orden) } : {})
                                },
                                title: "ordenes",
                            })
                        }}
                    />
                </TablePaginatorWrapper>
            </ReportWrapper>
            <OrdenesDetail isOpen={isOpen} onClose={closeDetail} data={selectedRow} />
        </>
    )
}

export default ReporteOrdenes
